import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { cn } from '@/utilities/cn'
import Image from 'next/image'
import React from 'react'

type Props = {
  data: SerializedEditorState
  enableGutter?: boolean
  enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

// Custom renderer for Lexical nodes with enhanced typography
const renderLexicalNode = (node: any, index: number): React.ReactNode => {
  if (!node) return null

  const key = `node-${index}`

  switch (node.type) {
    case 'text':
      // Handle text formatting with enhanced styling
      // Replace non-breaking spaces (\u00A0) with regular spaces to allow text wrapping
      let textElement: React.ReactNode = typeof node.text === 'string'
        ? node.text.replace(/\u00A0/g, ' ')
        : node.text

      if (node.format & 1) { // bold
        textElement = <strong key={key} className="font-semibold bg-gradient-to-r from-[#085e41] to-[#2D5A3D] bg-clip-text text-transparent">{textElement}</strong>
      }
      if (node.format & 2) { // italic
        textElement = <em key={key} className="italic">{textElement}</em>
      }
      if (node.format & 8) { // underline
        textElement = <u key={key} className="underline decoration-primary/40 underline-offset-2">{textElement}</u>
      }
      if (node.format & 4) { // strikethrough
        textElement = <s key={key} className="line-through text-muted-foreground">{textElement}</s>
      }
      if (node.format & 16) { // code
        textElement = (
          <code key={key} className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-[0.9em] text-primary">
            {textElement}
          </code>
        )
      }

      return textElement

    case 'linebreak':
      return <br key={key} />

    case 'paragraph':
      return (
        <p key={key} className="mb-6 text-base leading-relaxed text-muted-foreground [&:last-child]:mb-0">
          {node.children?.map((child: any, i: number) => renderLexicalNode(child, i))}
        </p>
      )

    case 'heading':
      // Extract the heading level — Payload may send 'h1'/'h2' or '1'/'2'
      const rawTag = node.tag || 'h2'
      const headingTag = rawTag.replace('h', '')
      const HeadingComponent = headingTag === '1' ? 'h1' :
                              headingTag === '2' ? 'h2' :
                              headingTag === '3' ? 'h3' :
                              headingTag === '4' ? 'h4' :
                              headingTag === '5' ? 'h5' :
                              headingTag === '6' ? 'h6' : 'h2'

      // Enhanced heading classes with better visual hierarchy
      const gradientText = 'bg-gradient-to-r from-[#085e41] to-[#2D5A3D] bg-clip-text text-transparent'
      const headingClass = headingTag === '1'
        ? `text-3xl sm:text-4xl font-bold tracking-tight ${gradientText} mb-6 mt-10 first:mt-0`
        : headingTag === '2'
        ? `text-2xl sm:text-3xl font-bold tracking-tight ${gradientText} mb-5 mt-10 first:mt-0`
        : headingTag === '3'
        ? `text-xl sm:text-2xl font-semibold ${gradientText} mb-4 mt-8 first:mt-0`
        : headingTag === '4'
        ? `text-lg sm:text-xl font-semibold ${gradientText} mb-3 mt-6 first:mt-0`
        : headingTag === '5'
        ? `text-base sm:text-lg font-semibold ${gradientText} mb-3 mt-5 first:mt-0`
        : `text-sm sm:text-base font-semibold tracking-wider ${gradientText} mb-3 mt-5 first:mt-0`

      return React.createElement(
        HeadingComponent,
        { key, className: headingClass },
        node.children?.map((child: any, i: number) => renderLexicalNode(child, i))
      )

    case 'list':
      const ListTag = node.listType === 'number' ? 'ol' : 'ul'
      const listClass = node.listType === 'number'
        ? 'list-decimal space-y-0.5 mb-6 pl-6 marker:text-primary marker:font-semibold'
        : 'list-disc space-y-0.5 mb-6 pl-6 marker:text-primary'
      return (
        <ListTag key={key} className={listClass}>
          {node.children?.map((child: any, i: number) => renderLexicalNode(child, i))}
        </ListTag>
      )

    case 'listitem':
      const hasNestedList = node.children?.some((child: any) => child.type === 'list')
      return (
        <li key={key} className={cn(
          "text-base leading-relaxed text-muted-foreground pl-1 [&>p]:mb-0 [&>ul]:mb-0 [&>ol]:mb-0",
          hasNestedList && "list-none"
        )}>
          {node.children?.map((child: any, i: number) => renderLexicalNode(child, i))}
        </li>
      )

    case 'link':
    case 'autolink':
      return (
        <a
          href={node.fields?.url || '#'}
          key={key}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:decoration-primary"
        >
          {node.children?.map((child: any, i: number) => renderLexicalNode(child, i))}
        </a>
      )

    case 'quote':
    case 'blockquote':
      return (
        <blockquote
          key={key}
          className="relative my-8 bg-primary/5 py-4 px-4 rounded-lg"
        >
          <div className="text-base leading-relaxed text-foreground/90">
            {node.children?.map((child: any, i: number) => renderLexicalNode(child, i))}
          </div>
        </blockquote>
      )

    case 'horizontalrule':
      return <hr key={key} className="my-10 border-t border-border/60" />

    case 'upload': {
      const image = node.value
      if (!image || typeof image !== 'object') return null

      const src = image.sizes?.large?.url || image.url
      const alt = image.alt || image.title || ''
      const width = image.sizes?.large?.width || image.width
      const height = image.sizes?.large?.height || image.height

      if (!src) return null

      const displaySize = node.fields?.displaySize || 'medium'

      const sizeClasses = {
        small: 'w-full my-4 sm:float-right sm:ml-6 sm:mb-4 sm:mt-1 sm:w-1/4 sm:max-w-[200px] sm:min-w-[120px]',
        medium: 'w-full my-4 sm:float-right sm:ml-6 sm:mb-4 sm:mt-1 sm:w-2/5 sm:max-w-[360px] sm:min-w-[180px]',
        large: 'w-full my-4 sm:float-right sm:ml-6 sm:mb-4 sm:mt-1 sm:w-1/2 sm:max-w-[480px] sm:min-w-[240px]',
        full: 'my-8 w-full',
      }

      return (
        <figure key={key} className={cn('overflow-visible not-prose', sizeClasses[displaySize as keyof typeof sizeClasses] || sizeClasses.medium)}>
          <Image
            src={src}
            alt={alt}
            width={width}
            height={height}
            className="h-auto w-full rounded-lg"
          />
          {image.caption?.root?.children?.length > 0 && (
            <figcaption className="mt-1.5 text-center text-xs text-muted-foreground">
              {image.caption.root.children.map((child: any, i: number) => renderLexicalNode(child, i))}
            </figcaption>
          )}
        </figure>
      )
    }

    default:
      // For unknown node types, try to render children if they exist
      if (node.children && Array.isArray(node.children)) {
        return (
          <div key={key}>
            {node.children.map((child: any, i: number) => renderLexicalNode(child, i))}
          </div>
        )
      }
      return null
  }
}

export const RichText: React.FC<Props> = (props) => {
  const { data, className, enableProse = true, enableGutter = true, ...rest } = props

  if (!data?.root?.children || !Array.isArray(data.root.children)) {
    return null
  }

  return (
    <div
      className={cn(
        '[&>p+ul]:mt-[-1rem] [&>p+ol]:mt-[-1rem] after:content-[""] after:block after:clear-both',
        {
          'container ': enableGutter,
          'max-w-none': !enableGutter,
          'prose prose-slate dark:prose-invert max-w-none [&>figure]:overflow-visible': enableProse,
        },
        className,
      )}
      {...rest}
    >
      {data.root.children.map((node, index) => renderLexicalNode(node, index))}
    </div>
  )
}

export default RichText
